import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store';
import {
  selectTradingHistory,
  selectTradingExportSettings,
  removeFromTradingHistory,
  clearTradingHistory,
  generatePineScriptFromLevels,
  setCurrentTradingLevels,
  setPineScriptModalOpen,
} from '../store';
import { TradingAnalysisResult } from '../types/trading';

interface TradingHistoryPanelProps {
  open: boolean;
  onClose: () => void;
}

const TradingHistoryPanel: React.FC<TradingHistoryPanelProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const tradingHistory = useAppSelector(selectTradingHistory);
  const exportSettings = useAppSelector(selectTradingExportSettings);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<TradingAnalysisResult | null>(null);

  // Filter history based on search term
  const filteredHistory = tradingHistory.filter(analysis =>
    analysis.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    analysis.aiResponseText.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, analysis: TradingAnalysisResult) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAnalysis(analysis);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAnalysis(null);
  };

  const handleViewPineScript = (analysis: TradingAnalysisResult) => {
    dispatch(setCurrentTradingLevels({
      tradingLevels: analysis.tradingLevels,
      assetName: analysis.assetName,
    }));
    dispatch(setPineScriptModalOpen(true));
    handleMenuClose();
  };

  const handleRegeneratePineScript = async (analysis: TradingAnalysisResult) => {
    try {
      await dispatch(generatePineScriptFromLevels({
        tradingLevels: analysis.tradingLevels,
        assetName: analysis.assetName,
        aiResponseText: analysis.aiResponseText, // Include original AI response
      })).unwrap();
      handleMenuClose();
    } catch (error) {
      console.error('Failed to regenerate Pine Script:', error);
    }
  };

  const handleDeleteAnalysis = (analysisId: string) => {
    dispatch(removeFromTradingHistory(analysisId));
    handleMenuClose();
  };

  const handleClearAllHistory = () => {
    dispatch(clearTradingHistory());
  };

  const handleExportAnalysis = (analysis: TradingAnalysisResult) => {
    const exportData = {
      assetName: analysis.assetName,
      timestamp: new Date(analysis.timestamp).toISOString(),
      tradingLevels: analysis.tradingLevels,
      pineScript: analysis.pineScriptResult.script,
      config: analysis.pineScriptResult.config,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trading-analysis-${analysis.assetName}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    handleMenuClose();
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getAnalysisStats = (analysis: TradingAnalysisResult) => {
    const { tradingLevels } = analysis;
    return {
      resistanceLevels: tradingLevels.resistanceLevels.length,
      supportLevels: tradingLevels.supportLevels.length,
      priceRange: tradingLevels.swingHigh - tradingLevels.swingLow,
    };
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400, md: 500 },
          maxWidth: '90vw',
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">
                Trading History
              </Typography>
              <Chip 
                label={tradingHistory.length} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by asset or analysis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Actions */}
          {tradingHistory.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearAllHistory}
                startIcon={<DeleteIcon />}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {filteredHistory.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              {tradingHistory.length === 0 ? (
                <Alert severity="info" sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    No Trading Analysis History
                  </Typography>
                  <Typography variant="body2">
                    Generate Pine Script code from AI trading analysis to see your history here.
                  </Typography>
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No results found for "{searchTerm}"
                </Typography>
              )}
            </Box>
          ) : (
            <List sx={{ p: 1 }}>
              {filteredHistory.map((analysis, index) => {
                const stats = getAnalysisStats(analysis);
                
                return (
                  <React.Fragment key={analysis.id}>
                    <ListItem
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {analysis.assetName}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                mb: 1,
                              }}
                            >
                              {analysis.aiResponseText}
                            </Typography>
                            
                            {/* Stats */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              <Chip
                                size="small"
                                icon={<TrendingUpIcon />}
                                label={`${stats.resistanceLevels} Resistance`}
                                variant="outlined"
                                color="error"
                              />
                              <Chip
                                size="small"
                                icon={<TrendingDownIcon />}
                                label={`${stats.supportLevels} Support`}
                                variant="outlined"
                                color="success"
                              />
                              <Chip
                                size="small"
                                icon={<AssessmentIcon />}
                                label={`Range: ${stats.priceRange.toFixed(2)}`}
                                variant="outlined"
                              />
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary">
                              {formatTimestamp(analysis.timestamp)}
                            </Typography>
                          </Box>
                          
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, analysis)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                        
                        {/* Warnings */}
                        {analysis.warnings && analysis.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                            {analysis.warnings.join(', ')}
                          </Alert>
                        )}
                      </Box>
                    </ListItem>
                    
                    {index < filteredHistory.length - 1 && <Divider sx={{ my: 1 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { minWidth: 200 }
          }}
        >
          <MenuItem onClick={() => selectedAnalysis && handleViewPineScript(selectedAnalysis)}>
            <CodeIcon sx={{ mr: 1 }} />
            View Pine Script
          </MenuItem>
          <MenuItem onClick={() => selectedAnalysis && handleRegeneratePineScript(selectedAnalysis)}>
            <CodeIcon sx={{ mr: 1 }} />
            Regenerate Pine Script
          </MenuItem>
          <MenuItem onClick={() => selectedAnalysis && handleExportAnalysis(selectedAnalysis)}>
            <DownloadIcon sx={{ mr: 1 }} />
            Export Analysis
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => selectedAnalysis && handleDeleteAnalysis(selectedAnalysis.id)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </Box>
    </Drawer>
  );
};

export default TradingHistoryPanel;